import * as RadixProgress from '@radix-ui/react-progress';
import { type ComponentProps, memo } from 'react';
import { useInView } from 'react-intersection-observer';

import { cn } from '@air/utils-tailwind';

import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import {
  CREDIT_USAGE_CATEGORIES,
  CREDIT_USAGE_CATEGORY_COLOR_MAP,
  getDisplayCategoryUsage,
  getPercentageUsed,
} from '~/components/CreditUsageMeter/utils';

export type CreditUsageMeterBarProps = Pick<ComponentProps<'div'>, 'className'> & {
  /** When provided and > 0, folds gift credits into the total the fill is measured against (matches the count). */
  giftCreditsBalance?: number;
} & Pick<CreditUsageStats, 'allocated' | 'used'>;

export const CreditUsageMeterBar = memo(
  ({ allocated, used, className, giftCreditsBalance = 0 }: CreditUsageMeterBarProps) => {
    const [ref, inView] = useInView();

    if (!allocated) {
      return null;
    }

    // Gift credits are spendable, so the fill is measured against allocation + gift to stay in sync with the count.
    const totalAllocated = allocated + giftCreditsBalance;

    return (
      <RadixProgress.Root
        data-testid="CREDIT_USAGE_METER_BAR"
        className={cn(
          'h-2 w-full overflow-hidden rounded-full ads:bg-input bg-gray-3 will-change-transform',
          className,
        )}
        // biome-ignore lint/style/noMagicNumbers: percentage calculation
        value={Math.min(100, getPercentageUsed(used.total, totalAllocated))}
      >
        <RadixProgress.Indicator className="flex h-full" ref={ref}>
          {CREDIT_USAGE_CATEGORIES.map((category) => {
            const creditsUsed = getDisplayCategoryUsage(used, category).credits;

            // Omit categories with no credit usage
            if (!creditsUsed) {
              return null;
            }

            return (
              <div
                className={cn(CREDIT_USAGE_CATEGORY_COLOR_MAP[category])}
                key={category}
                style={{
                  transition: 'width 660ms cubic-bezier(0.65, 0, 0.35, 1)',
                  width: inView ? `${getPercentageUsed(creditsUsed, totalAllocated)}%` : '0%',
                }}
              />
            );
          })}
        </RadixProgress.Indicator>
      </RadixProgress.Root>
    );
  },
);

CreditUsageMeterBar.displayName = 'CreditUsageMeterBar';
